import { num } from './unityProps.ts';

/** 2D affine transform: x' = a x + c y + tx, y' = b x + d y + ty */
export interface Xform {
  a: number; b: number; c: number; d: number; tx: number; ty: number; // 2D affine (x' = a x + c y + tx)
}

export function localXform(t: any): Xform {
  const p = t?.m_LocalPosition ?? {};
  const q = t?.m_LocalRotation ?? {};
  const s = t?.m_LocalScale ?? {};
  const qz = num(q.z), qw = num(q.w, 1), qx = num(q.x), qy = num(q.y);
  // z rotation from quaternion (ignores x/y tilt which 2D content never uses meaningfully)
  const angle = Math.atan2(2 * (qw * qz + qx * qy), 1 - 2 * (qy * qy + qz * qz));
  const sx = num(s.x, 1), sy = num(s.y, 1);
  const cos = Math.cos(angle), sin = Math.sin(angle);
  return { a: cos * sx, b: sin * sx, c: -sin * sy, d: cos * sy, tx: num(p.x), ty: num(p.y) };
}

export function mul(m: Xform, n: Xform): Xform {
  return {
    a: m.a * n.a + m.c * n.b,
    b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d,
    d: m.b * n.c + m.d * n.d,
    tx: m.a * n.tx + m.c * n.ty + m.tx,
    ty: m.b * n.tx + m.d * n.ty + m.ty,
  };
}

export const IDENTITY: Xform = { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };

export function xformAngleDeg(m: Xform): number {
  return (Math.atan2(m.b, m.a) * 180) / Math.PI;
}
export function xformScale(m: Xform): [number, number] {
  const sx = Math.hypot(m.a, m.b);
  const det = m.a * m.d - m.b * m.c;
  return [sx, det / (sx || 1)];
}

export function apply(m: Xform, x: number, y: number): [number, number] {
  return [m.a * x + m.c * y + m.tx, m.b * x + m.d * y + m.ty];
}

export function invert(m: Xform): Xform {
  const det = m.a * m.d - m.b * m.c || 1e-9;
  const a = m.d / det, b = -m.b / det, c = -m.c / det, d = m.a / det;
  return { a, b, c, d, tx: -(a * m.tx + c * m.ty), ty: -(b * m.tx + d * m.ty) };
}

export function compose(x: number, y: number, rotDeg: number, sx = 1, sy = 1): Xform {
  const r = (rotDeg * Math.PI) / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  return { a: cos * sx, b: sin * sx, c: -sin * sy, d: cos * sy, tx: x, ty: y };
}
